import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';

export type ChipColor = 'brand' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

/** Chip / tag pill with an optional `chipIcon`, colour tones and a removable variant that emits `remove`. */
@Component({
  selector: 'zag-chip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ZagIcon],
  template: `
    <ng-content select="[chipIcon]" />
    <span class="zag-chip__label"><ng-content /></span>
    @if (removable()) {
      <button type="button" class="zag-chip__remove" (click)="onRemove($event)" [attr.aria-label]="effectiveRemoveLabel()">
        <span zagIcon="close" class="zag-chip__remove-icon"></span>
      </button>
    }
  `,
  styleUrl: './chip.scss',
  host: {
    '[class.zag-chip]': 'true',
    '[class.zag-chip--brand]': 'color() === "brand"',
    '[class.zag-chip--success]': 'color() === "success"',
    '[class.zag-chip--warning]': 'color() === "warning"',
    '[class.zag-chip--error]': 'color() === "error"',
    '[class.zag-chip--info]': 'color() === "info"',
    '[class.zag-chip--neutral]': 'color() === "neutral"',
    '[class.zag-chip--removable]': 'removable()',
    '[class.zag-chip--dashed]': 'dashed()',
  },
})
export class Chip {
  private readonly translate = inject(TranslateService);
  private readonly _currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  readonly color = input<ChipColor>('neutral');
  readonly removable = input(false, { transform: booleanAttribute });
  /** Dashed outline for provisional states such as requested or part-day entries; pair it with text or an icon. */
  readonly dashed = input(false, { transform: booleanAttribute });
  /** Accessible label for the remove button. Defaults to the `common.remove` translation key. */
  readonly removeLabel = input<string>('');

  protected readonly effectiveRemoveLabel = computed(() => {
    this._currentLang();
    return this.removeLabel() || this.translate.instant('common.remove');
  });

  readonly remove = output<void>();

  protected onRemove(event: MouseEvent): void {
    event.stopPropagation();
    this.remove.emit();
  }
}
